// Regras de fila e de ação do saque na visão admin (`spec/09-withdrawals.md` §4.7,
// `spec/12-admin-api.md` §4.2). Funções puras: o teste não precisa de rede nem de React.

import type {
  AdminWithdrawalDetail,
  AdminWithdrawalStatus,
} from '../../api/queries/admin-withdrawals.ts';
import type { AdminWithdrawalTotals } from './queries.ts';

/**
 * Abas do croqui (`screens/admin/saques.tsx`) mais os status que ele omite. O croqui tem
 * cinco (aguardando · processando · pagos · falhos · rejeitados); a máquina de estados
 * tem sete (`spec/09` §4.4) e a API filtra por todos, então `approved` e `cancelled`
 * ganham aba, sem elas `?status=approved` seria rota sem caminho na tela. `all` é a aba
 * onde o recorte por seller (`?seller_id=`) faz sentido. Marca #80 no croqui.
 */
export const TAB_IDS = [
  'all',
  'awaiting',
  'approved',
  'processing',
  'successful',
  'failed',
  'rejected',
  'cancelled',
] as const;
export type TabId = (typeof TAB_IDS)[number];

/** Plural, como as abas do croqui ("Pagos", "Falhos", "Rejeitados"). */
export const TAB_LABEL: Record<TabId, string> = {
  all: 'Todos',
  awaiting: 'Aguardando',
  approved: 'Aprovados',
  processing: 'Processando',
  successful: 'Pagos',
  failed: 'Falhos',
  rejected: 'Rejeitados',
  cancelled: 'Cancelados',
};

/**
 * Contagem e soma da aba, de `totals.by_status` (API-27, `with_totals=1`). `cents` soma
 * `amount` sem a taxa, é o mesmo número de `overview.withdrawals.awaiting_cents`. O
 * agregado ignora o filtro de `status` da página, então uma consulta alimenta as oito
 * abas. `undefined` enquanto a página não chegou: a aba não inventa zero.
 */
export function tabTotals(
  totals: AdminWithdrawalTotals | undefined,
  tab: TabId,
): { count: number; cents: number } | undefined {
  if (totals === undefined) {
    return undefined;
  }
  const entries = Object.entries(totals.by_status) as [
    AdminWithdrawalStatus,
    { count: number; cents: number },
  ][];
  const counted = tab === 'all' ? entries : entries.filter(([status]) => status === tab);
  return counted.reduce(
    (sum, [, value]) => ({ count: sum.count + value.count, cents: sum.cents + value.cents }),
    { count: 0, cents: 0 },
  );
}

/** Frase exata de `spec/09` §4.7 (`MARK_FAILED_CONFIRM` da API). Sem ela o botão não sai. */
export const MARK_FAILED_CONFIRM = 'I_CHECKED_WITH_PARTNER';

/** `reasonBodySchema` da API: `z.string().min(10).max(1000)`. */
export const REASON_MIN_LENGTH = 10;
export const REASON_MAX_LENGTH = 1000;

/** Aprovar e rejeitar só existem em `awaiting` (`409 invalid_state` fora dele). */
export function canDecide(status: AdminWithdrawalStatus): boolean {
  return status === 'awaiting';
}

/** Poll e mark-failed só existem em `processing`. */
export function canPoll(status: AdminWithdrawalStatus): boolean {
  return status === 'processing';
}

export function canMarkFailed(status: AdminWithdrawalStatus): boolean {
  return status === 'processing';
}

/**
 * A API não diz se a chave veio completa: `withdrawal.pix_key.key` é **sempre** mascarada
 * (`serializeWithdrawal`), e só a `pix_key` do topo vira a chave real com step-up recente
 * (`spec/12` §4.2). Comparar as duas é o único jeito honesto de saber o que está na tela -
 * e volta sozinho para "mascarada" quando a janela de 5 min expira.
 */
export function isKeyRevealed(detail: AdminWithdrawalDetail): boolean {
  return detail.pix_key.key !== detail.withdrawal.pix_key.key;
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * Idade da linha na fila, curta o bastante para caber numa coluna (`2 h`, `3 d`). `now`
 * entra por parâmetro para o teste não depender do relógio da máquina.
 */
export function ageOf(iso: string, now: Date = new Date()): string {
  const elapsed = now.getTime() - new Date(iso).getTime();
  if (!Number.isFinite(elapsed) || elapsed < MINUTE_MS) {
    return 'agora';
  }
  if (elapsed < HOUR_MS) {
    return `${Math.floor(elapsed / MINUTE_MS)} min`;
  }
  if (elapsed < DAY_MS) {
    return `${Math.floor(elapsed / HOUR_MS)} h`;
  }
  return `${Math.floor(elapsed / DAY_MS)} d`;
}
